import { DEFAULT_LOCALE, type Locale } from './i18n/messages';

export type ImageToolId =
  | 'image-converter'
  | 'shadow-remover'
  | 'image-upscaler'
  | 'photo-darkener'
  | 'photo-inverter'
  | 'photo-blur'
  | 'round-cropper'
  | 'photo-cropper'
  | 'color-eyedropper'
  | 'black-and-white'
  | 'photo-flipper'
  | 'photo-brightener'
  | 'colorize-photo'
  | 'photo-rotator';

export type ImageToolDefinition = {
  id: ImageToolId;
  slug: string;
  title: string;
  shortTitle: string;
  eyebrow: string;
  description: string;
  outputNote: string;
  seoTitle: string;
  seoDescription: string;
  headline: string;
  heroBody: string;
  whenToUse: string[];
  tips: string[];
};

export type ImageToolsPageCopy = {
  routeSegment: string;
  seoTitle: string;
  seoDescription: string;
  heroTitle: string;
  heroBody: string;
  eyebrow: string;
  backgroundRemoverTitle: string;
  backgroundRemoverDescription: string;
  imageAlt: string;
};

export const IMAGE_TOOLS_LASTMOD = '2026-06-01';

export const IMAGE_TOOLS: ImageToolDefinition[] = [
  {
    id: 'image-converter',
    slug: 'image-converter',
    title: 'Image format converter',
    shortTitle: 'Image converter',
    eyebrow: 'Convert',
    description: 'Convert JPG, PNG, WebP, AVIF, GIF, or BMP files when your browser can decode them.',
    outputNote: 'PNG is always available. JPEG, WebP, and AVIF are shown only when this browser can encode them.',
    seoTitle: 'Free Image Format Converter Online',
    seoDescription:
      'Convert image files online in your browser. Batch convert supported JPG, PNG, WebP, AVIF, GIF, and BMP uploads to PNG, JPEG, WebP, or AVIF.',
    headline: 'Convert image formats in the browser.',
  heroBody:
      'Drop in one image or a batch, choose the output format, and download converted files without uploading the originals.',
    whenToUse: [
      'A form accepts PNG but your phone saved a JPEG or WebP.',
      'You need a JPEG copy of a transparent PNG or a PNG copy of a JPEG.',
      'A batch of images needs the same browser-supported output format.',
    ],
    tips: [
      'For JPEG output, transparent pixels are placed over white because JPEG has no alpha channel.',
      'Advanced formats depend on browser codec support; unsupported formats show a clear error.',
      'For passport, visa, or ID files, check the final size and format in the Passlens editor before submission.',
    ],
  },
  {
    id: 'shadow-remover',
    slug: 'shadow-remover',
    title: 'Shadow remover',
    shortTitle: 'Shadow remover',
    eyebrow: 'Light cleanup',
    description: 'Lift mild face and paper shadows with a local brightness and contrast pass before you export.',
    outputNote: 'Best for small lighting problems. Heavy shadows still need a better source photo.',
    seoTitle: 'Free Shadow Remover for Photos Online',
    seoDescription:
      'Use the Passlens shadow remover to lighten mild photo shadows in your browser before exporting a PNG or moving into the ID photo editor.',
    headline: 'Remove mild shadows from a photo before you export.',
    heroBody:
      'Open a JPG, PNG, or WebP and lift soft shadows in the browser. It is best for small lighting problems on portraits, paper scans, and product shots.',
    whenToUse: [
      'A face, document, or product photo is a little too dark on one side.',
      'A phone scan picked up desk shadows that make the page look uneven.',
      'You want to clean up a photo before checking passport or ID sizing in Passlens.',
    ],
    tips: [
      'Start with a low strength setting and increase it only until the subject is readable.',
      'Retake the photo if the shadow hides facial detail or document text.',
      'Use the full Passlens editor after cleanup when the image needs an official size.',
    ],
  },
  {
    id: 'photo-darkener',
    slug: 'photo-darkener',
    title: 'Photo darkener',
    shortTitle: 'Darken photo',
    eyebrow: 'Tone',
    description: 'Darken an over-bright image while keeping the workflow local.',
    outputNote: 'Useful when a scan or phone photo is washed out.',
    seoTitle: 'Darken Photo Online for Free',
    seoDescription:
      'Darken an over-bright photo in the browser and export a PNG. The Passlens photo darkener works locally with JPG, PNG, and WebP files.',
    headline: 'Darken a washed-out photo.',
    heroBody:
      'Reduce brightness on an overexposed portrait, scan, or product image before you download it or continue into the Passlens editor.',
    whenToUse: [
      'A phone photo looks washed out after flash or window light.',
      'A scan lost contrast and needs a darker preview.',
      'You want a quick tone adjustment before export.',
    ],
    tips: [
      'Avoid pushing the slider so far that hair, clothing, or document edges block up.',
      'If the background turns grey, retake the photo with softer light.',
      'Use official photo presets after the tone fix when compliance matters.',
    ],
  },
  {
    id: 'photo-inverter',
    slug: 'photo-inverter',
    title: 'Photo inverter',
    shortTitle: 'Invert photo',
    eyebrow: 'Invert',
    description: 'Invert image colors for quick inspection, document cleanup, or creative checks.',
    outputNote: 'Export keeps the inverted colors as a PNG.',
    seoTitle: 'Invert Photo Colors Online',
    seoDescription:
      'Invert photo colors online in the browser. Upload a JPG, PNG, or WebP, preview the inverted result, and download a PNG.',
    headline: 'Invert photo colors quickly.',
    heroBody:
      'Flip light and dark values for a fast inspection pass, document check, or creative edit. The preview and export happen in your browser.',
    whenToUse: [
      'You need to inspect faint text, edges, or contrast.',
      'A document scan is easier to review with inverted tones.',
      'You want a quick creative color inversion without opening a full editor.',
    ],
    tips: [
      'Inverted images are usually not valid for passport or ID submissions.',
      'Download the PNG only after checking the preview at a comfortable zoom.',
      'Use the normal Passlens editor for final document-photo output.',
    ],
  },
  {
    id: 'photo-blur',
    slug: 'photo-blur',
    title: 'Photo blurring tool',
    shortTitle: 'Blur photo',
    eyebrow: 'Blur',
    description: 'Blur an image locally before sharing, saving, or using it as a soft background.',
    outputNote: 'Blur strength is adjustable before export.',
    seoTitle: 'Blur Photo Online for Free',
    seoDescription:
      'Blur a photo online in the browser and export a PNG. Use Passlens for quick blur effects on JPG, PNG, and WebP images.',
    headline: 'Blur a photo before sharing or reuse.',
    heroBody:
      'Add a soft blur to a photo for previews, backgrounds, or private sharing. Adjust the strength and download a PNG without uploading the file.',
    whenToUse: [
      'A background needs to be softened for a graphic or mockup.',
      'You want to obscure detail before sending a sample image.',
      'A profile or product image needs a simple blur effect.',
    ],
    tips: [
      'Blur is applied to the whole image on this page.',
      'Use background removal if you need to isolate the subject instead.',
      'For ID photos, blur effects are not acceptable for the final submission.',
    ],
  },
  {
    id: 'round-cropper',
    slug: 'round-cropper',
    title: 'Round cropping tool',
    shortTitle: 'Round cropper',
    eyebrow: 'Circle crop',
    description: 'Make a circular crop for profiles and contact photos.',
    outputNote: 'The export is a transparent PNG with the round crop applied.',
    seoTitle: 'Round Crop Photo Online',
    seoDescription:
      'Round crop a photo online and download a transparent PNG for profile pictures, contact photos, and simple avatar images.',
    headline: 'Make a round crop from any photo.',
    heroBody:
      'Create a circular crop for profile pictures, contact images, and avatars. Passlens exports the result as a transparent PNG.',
    whenToUse: [
      'You need a round profile image for a website, app, or contact card.',
      'A square crop would show too much background around the face.',
      'You want a transparent PNG instead of a flat circle on a solid color.',
    ],
    tips: [
      'Start with a centered subject so the circle crop does not cut off the face.',
      'Use background removal first if the original background is distracting.',
      'Use the main editor for official ID crops, which are usually rectangular.',
    ],
  },
  {
    id: 'photo-cropper',
    slug: 'photo-cropper',
    title: 'Photo cropper',
    shortTitle: 'Photo cropper',
    eyebrow: 'Crop',
    description: 'Create a centered square, portrait, or landscape crop without leaving the page.',
    outputNote: 'For official ID crops, use the main Passlens editor with document presets.',
    seoTitle: 'Crop Photo Online for Free',
    seoDescription:
      'Crop a photo online in the browser. Choose square, portrait, landscape, or wide crop shapes and download a PNG.',
    headline: 'Crop a photo into a clean shape.',
    heroBody:
      'Pick a square, portrait, landscape, or wide crop and export a PNG. Use it for quick edits, then switch to document presets when sizing must be exact.',
    whenToUse: [
      'A photo needs a cleaner square, portrait, or landscape frame.',
      'You want to crop before making a profile image or simple graphic.',
      'A source image has too much empty space around the subject.',
    ],
    tips: [
      'This cropper uses simple centered crops.',
      'Use Passlens document presets when head position and millimetres matter.',
      'Check the downloaded PNG before uploading it anywhere official.',
    ],
  },
  {
    id: 'color-eyedropper',
    slug: 'color-eyedropper',
    title: 'Color eyedropper',
    shortTitle: 'Color eyedropper',
    eyebrow: 'Sample',
    description: 'Click the preview to read a color from the image.',
    outputNote: 'Picked colors show as a swatch, HEX, and RGBA.',
    seoTitle: 'Color Eyedropper from Image Online',
    seoDescription:
      'Pick a color from an image in the browser. Upload a JPG, PNG, or WebP and click the preview to see the swatch, HEX, and RGBA values.',
    headline: 'Pick a color from an image.',
    heroBody:
      'Upload a photo, click the preview, and copy the color value you need. Passlens shows the sampled color with HEX and RGBA values.',
    whenToUse: [
      'You need to match a background, brand color, paper tone, or clothing color.',
      'A designer or support request asks for a HEX or RGBA value.',
      'You want to sample a color without installing a separate editor.',
    ],
    tips: [
      'Click a flat area of the image for the cleanest sample.',
      'Small JPEG artifacts can change nearby pixels, so sample a few points.',
      'Use official background-color rules when the image is for a passport or visa.',
    ],
  },
  {
    id: 'black-and-white',
    slug: 'black-and-white',
    title: 'Black and white photo editor',
    shortTitle: 'Black and white',
    eyebrow: 'Greyscale',
    description: 'Convert a photo to black and white locally.',
    outputNote: 'Export keeps the greyscale result as a PNG.',
    seoTitle: 'Black and White Photo Editor Online',
    seoDescription:
      'Convert a photo to black and white online in your browser. Upload a JPG, PNG, or WebP and download a greyscale PNG.',
    headline: 'Turn a photo black and white.',
    heroBody:
      'Create a greyscale PNG from a portrait, scan, or product image. The conversion is local and works directly in the browser.',
    whenToUse: [
      'A profile image or document preview needs a greyscale version.',
      'You want to inspect contrast without color getting in the way.',
      'A design layout calls for a black-and-white image.',
    ],
    tips: [
      'Most modern passport and visa systems require color photos.',
      'Use this for drafts, previews, or non-official images.',
      'Check contrast after conversion so the subject does not look flat.',
    ],
  },
  {
    id: 'photo-flipper',
    slug: 'photo-flipper',
    title: 'Photo flipper',
    shortTitle: 'Photo flipper',
    eyebrow: 'Mirror',
    description: 'Flip an image horizontally or vertically.',
    outputNote: 'Useful for product previews and non-official profile images.',
    seoTitle: 'Flip Photo Online for Free',
    seoDescription:
      'Flip a photo horizontally or vertically in the browser and download a PNG. Use Passlens for quick local image flipping.',
    headline: 'Flip a photo horizontally or vertically.',
    heroBody:
      'Mirror a photo for product previews, layouts, or quick corrections. Choose the direction and export a PNG without uploading anything.',
    whenToUse: [
      'A product image faces the wrong way for a layout.',
      'A preview needs a horizontal or vertical mirror.',
      'You want a quick flip before cropping or resizing.',
    ],
    tips: [
      'Do not use mirrored output for official passport or ID submissions.',
      'Check text and logos after flipping because they will read backwards.',
      'Use rotate instead if the photo is turned sideways.',
    ],
  },
  {
    id: 'photo-brightener',
    slug: 'photo-brightener',
    title: 'Photo brightener',
    shortTitle: 'Photo brightener',
    eyebrow: 'Tone',
    description: 'Brighten a dark image with a local adjustment.',
    outputNote: 'Works best on underexposed images that still have visible detail.',
    seoTitle: 'Brighten Photo Online for Free',
    seoDescription:
      'Brighten a dark photo online in your browser and export a PNG. Passlens works locally with JPG, PNG, and WebP files.',
    headline: 'Brighten a dark photo before export.',
    heroBody:
      'Lift an underexposed image enough to inspect it, reuse it, or continue into the Passlens editor. The adjustment happens in your browser.',
    whenToUse: [
      'A portrait, scan, or product photo is too dark but still has detail.',
      'You need a quick brightness pass before cropping or background removal.',
      'A preview image is hard to judge on a dark screen.',
    ],
    tips: [
      'Do not brighten so much that skin, paper, or highlights turn flat white.',
      'If the image is blurry or noisy, retaking it will usually work better.',
      'Use document presets after brightening when the final file needs official sizing.',
    ],
  },
  {
    id: 'colorize-photo',
    slug: 'colorize-photo',
    title: 'Colorize photo tool',
    shortTitle: 'Colorize photo',
    eyebrow: 'Tint',
    description: 'Add a controlled color tint to a black-and-white style output.',
    outputNote: 'This is a browser tint effect, not historical photo restoration.',
    seoTitle: 'Colorize Photo Online with a Simple Tint',
    seoDescription:
      'Colorize a photo online with a simple browser tint effect. Upload an image, adjust the strength, and export a PNG.',
    headline: 'Add a simple color tint to a photo.',
    heroBody:
      'Create a tinted PNG from a portrait, product image, or scan. This is a fast browser effect for previews and graphics.',
    whenToUse: [
      'A black-and-white style image needs a controlled tint.',
      'You want a quick color treatment for a social or design preview.',
      'A product or profile image needs a warmer or cooler draft look.',
    ],
    tips: [
      'This is not restoration and will not recover original colors.',
      'Keep the strength low when faces need to look natural.',
      'Use normal color output for passport, visa, and ID photos.',
    ],
  },
  {
    id: 'photo-rotator',
    slug: 'photo-rotator',
    title: 'Photo rotator',
    shortTitle: 'Photo rotator',
    eyebrow: 'Rotate',
    description: 'Rotate a photo by small or large angles and export the result.',
    outputNote: 'For passport-photo straightening, the main editor still gives document overlays.',
    seoTitle: 'Rotate Photo Online for Free',
    seoDescription:
      'Rotate a photo online in the browser and export a PNG. Use Passlens for quick image rotation before cropping or ID photo sizing.',
    headline: 'Rotate a photo before you crop or export.',
    heroBody:
      'Straighten a tilted scan, portrait, or product image with a browser rotation control. Download a PNG or continue into Passlens for official sizing.',
    whenToUse: [
      'A phone photo or scan is slightly tilted.',
      'An image is turned sideways and needs a quick correction.',
      'You want to rotate before crop, background removal, or print layout.',
    ],
    tips: [
      'Small rotations can create transparent corners in the PNG.',
      'Use crop after rotation if you need a clean rectangular frame.',
      'For passport photos, check the head guides in the main editor before export.',
    ],
  },
];

const IMAGE_TOOLS_PAGE_COPY: Record<Locale, ImageToolsPageCopy> = {
  en: {
    routeSegment: 'image-tools',
    seoTitle: 'Free Online Image Tools',
    seoDescription:
      'Use free browser-based image tools for format conversion, crop, round crop, rotate, flip, brighten, darken, blur, invert, black-and-white, color picking, tinting, shadow cleanup, and background removal.',
    heroTitle: 'Quick browser tools for everyday photo fixes.',
    heroBody:
      'Crop, brighten, blur, flip, rotate, sample colors, and export a clean PNG without sending the image anywhere. Use these tools for simple edits, then move into the Passlens editor when the photo needs official passport, visa, or ID sizing.',
    eyebrow: 'Image tools',
    backgroundRemoverTitle: 'Free background remover',
    backgroundRemoverDescription: 'Transparent PNG cutouts with no signup or watermark.',
    imageAlt: 'Browser image tools workbench with crop, color, black-and-white, and profile-photo previews',
  },
  es: {
    routeSegment: 'herramientas-imagen',
    seoTitle: 'Herramientas de imagen online gratis',
    seoDescription:
      'Usa herramientas de imagen gratis en el navegador para convertir formatos, recortar, girar, aclarar, oscurecer, desenfocar, invertir, muestrear colores y quitar fondos.',
    heroTitle: 'Herramientas rápidas para arreglos cotidianos de fotos.',
    heroBody:
      'Recorta, aclara, desenfoca, invierte, gira, toma muestras de color y exporta un PNG limpio. Usa estas herramientas para ajustes rápidos y pasa al editor de Passlens cuando necesites tamaño oficial.',
    eyebrow: 'Herramientas de imagen',
    backgroundRemoverTitle: 'Eliminador de fondo',
    backgroundRemoverDescription: 'Recortes PNG transparentes gratis para productos, retratos y documentos.',
    imageAlt: 'Área de trabajo de herramientas de imagen con recorte, color y vista previa de foto de perfil',
  },
  fr: {
    routeSegment: 'outils-image',
    seoTitle: 'Outils d’image en ligne gratuits',
    seoDescription:
      'Utilisez des outils d’image gratuits dans le navigateur pour convertir, recadrer, pivoter, éclaircir, assombrir, flouter, inverser, échantillonner des couleurs et supprimer un fond.',
    heroTitle: 'Des outils rapides pour les retouches photo du quotidien.',
    heroBody:
      'Recadrez, éclaircissez, floutez, inversez, pivotez, prélevez une couleur et exportez un PNG propre. Passez ensuite dans l’éditeur Passlens si la photo doit respecter un format officiel.',
    eyebrow: 'Outils d’image',
    backgroundRemoverTitle: 'Suppression du fond',
    backgroundRemoverDescription: 'Détourages PNG transparents gratuits pour produits, portraits et documents.',
    imageAlt: 'Interface d’outils image avec recadrage, couleur et aperçu de photo de profil',
  },
  ar: {
    routeSegment: 'ادوات-الصور',
    seoTitle: 'أدوات صور مجانية عبر الإنترنت',
    seoDescription:
      'استخدم أدوات صور مجانية في المتصفح لتحويل الصيغ والقص والتدوير والتفتيح والتغميق والتمويه والعكس واختيار الألوان وإزالة الخلفية.',
    heroTitle: 'أدوات سريعة لإصلاح الصور اليومية.',
    heroBody:
      'قص الصورة أو عدّل اللون أو دوّرها أو اختر لونًا منها ثم صدّر PNG نظيفًا. استخدم محرر Passlens عندما تحتاج الصورة إلى مقاس رسمي.',
    eyebrow: 'أدوات الصور',
    backgroundRemoverTitle: 'إزالة الخلفية',
    backgroundRemoverDescription: 'قصّ PNG شفاف مجاني للمنتجات والصور الشخصية والمستندات.',
    imageAlt: 'واجهة أدوات الصور مع القص والألوان ومعاينة صورة ملف شخصي',
  },
  'zh-Hans': {
    routeSegment: '图片工具',
    seoTitle: '免费在线图片工具',
    seoDescription:
      '在浏览器中使用免费图片工具，转换格式、裁剪、旋转、调亮、调暗、模糊、反色、取色并移除背景。',
    heroTitle: '用于日常照片修整的快速浏览器工具。',
    heroBody:
      '裁剪、调亮、模糊、反色、旋转、取色并导出干净的 PNG。需要官方护照、签证或证件照尺寸时，再进入 Passlens 编辑器。',
    eyebrow: '图片工具',
    backgroundRemoverTitle: '背景移除',
    backgroundRemoverDescription: '为商品、人像和文档免费生成透明 PNG 抠图。',
    imageAlt: '图片工具工作区，包含裁剪、颜色和头像预览',
  },
  de: {
    routeSegment: 'bild-tools',
    seoTitle: 'Kostenlose Online-Bildtools',
    seoDescription:
      'Nutzen Sie kostenlose Bildtools im Browser zum Konvertieren, Zuschneiden, Drehen, Aufhellen, Abdunkeln, Weichzeichnen, Invertieren, Farbmessen und Entfernen von Hintergründen.',
    heroTitle: 'Schnelle Browser-Tools für alltägliche Fotokorrekturen.',
    heroBody:
      'Zuschneiden, aufhellen, weichzeichnen, invertieren, drehen, Farben aufnehmen und ein sauberes PNG exportieren. Für offizielle Größen wechseln Sie danach in den Passlens-Editor.',
    eyebrow: 'Bildtools',
    backgroundRemoverTitle: 'Hintergrundentferner',
    backgroundRemoverDescription: 'Kostenlose transparente PNG-Freisteller für Produkte, Porträts und Dokumente.',
    imageAlt: 'Bildtools-Arbeitsbereich mit Zuschnitt, Farbe und Profilfoto-Vorschau',
  },
  pt: {
    routeSegment: 'ferramentas-de-imagem',
    seoTitle: 'Ferramentas de imagem online gratuitas',
    seoDescription:
      'Use ferramentas de imagem grátis no navegador para converter formatos, recortar, girar, clarear, escurecer, desfocar, inverter, pegar cores e remover fundo.',
    heroTitle: 'Ferramentas rápidas para ajustes comuns de foto.',
    heroBody:
      'Recorte, clareie, desfoque, inverta, gire, colete cores e exporte um PNG limpo. Use o editor Passlens quando a foto precisar de tamanho oficial.',
    eyebrow: 'Ferramentas de imagem',
    backgroundRemoverTitle: 'Removedor de fundo',
    backgroundRemoverDescription: 'Recortes PNG transparentes grátis para produtos, retratos e documentos.',
    imageAlt: 'Área de ferramentas de imagem com recorte, cor e prévia de foto de perfil',
  },
};

const LOCALIZED_TOOL_SLUGS: Record<Locale, Record<ImageToolId, string>> = {
  en: Object.fromEntries(IMAGE_TOOLS.map((tool) => [tool.id, tool.slug])) as Record<ImageToolId, string>,
  es: {
    'image-converter': 'conversor-de-imagenes',
    'shadow-remover': 'eliminar-sombras',
    'image-upscaler': 'mejorar-resolucion-imagen',
    'photo-darkener': 'oscurecer-foto',
    'photo-inverter': 'invertir-foto',
    'photo-blur': 'desenfocar-foto',
    'round-cropper': 'recorte-redondo',
    'photo-cropper': 'recortar-foto',
    'color-eyedropper': 'cuentagotas-color',
    'black-and-white': 'foto-blanco-y-negro',
    'photo-flipper': 'voltear-foto',
    'photo-brightener': 'aclarar-foto',
    'colorize-photo': 'colorear-foto',
    'photo-rotator': 'rotar-foto',
  },
  fr: {
    'image-converter': 'convertisseur-image',
    'shadow-remover': 'supprimer-ombres',
    'image-upscaler': 'agrandir-image',
    'photo-darkener': 'assombrir-photo',
    'photo-inverter': 'inverser-photo',
    'photo-blur': 'flouter-photo',
    'round-cropper': 'recadrage-rond',
    'photo-cropper': 'recadrer-photo',
    'color-eyedropper': 'pipette-couleur',
    'black-and-white': 'photo-noir-et-blanc',
    'photo-flipper': 'retourner-photo',
    'photo-brightener': 'eclaircir-photo',
    'colorize-photo': 'coloriser-photo',
    'photo-rotator': 'rotation-photo',
  },
  ar: {
    'image-converter': 'محول-الصور',
    'shadow-remover': 'ازالة-الظل',
    'image-upscaler': 'تكبير-الصورة',
    'photo-darkener': 'تغميق-الصورة',
    'photo-inverter': 'عكس-الصورة',
    'photo-blur': 'تمويه-الصورة',
    'round-cropper': 'قص-دائري',
    'photo-cropper': 'قص-الصورة',
    'color-eyedropper': 'قطارة-اللون',
    'black-and-white': 'ابيض-واسود',
    'photo-flipper': 'قلب-الصورة',
    'photo-brightener': 'تفتيح-الصورة',
    'colorize-photo': 'تلوين-الصورة',
    'photo-rotator': 'تدوير-الصورة',
  },
  'zh-Hans': {
    'image-converter': '图片格式转换器',
    'shadow-remover': '去除阴影',
    'image-upscaler': '图片放大',
    'photo-darkener': '照片调暗',
    'photo-inverter': '照片反色',
    'photo-blur': '照片模糊',
    'round-cropper': '圆形裁剪',
    'photo-cropper': '照片裁剪',
    'color-eyedropper': '颜色取样器',
    'black-and-white': '黑白照片',
    'photo-flipper': '照片翻转',
    'photo-brightener': '照片调亮',
    'colorize-photo': '照片上色',
    'photo-rotator': '照片旋转',
  },
  de: {
    'image-converter': 'bildkonverter',
    'shadow-remover': 'schatten-entfernen',
    'image-upscaler': 'bild-hochskalieren',
    'photo-darkener': 'foto-abdunkeln',
    'photo-inverter': 'foto-invertieren',
    'photo-blur': 'foto-weichzeichnen',
    'round-cropper': 'rund-zuschneiden',
    'photo-cropper': 'foto-zuschneiden',
    'color-eyedropper': 'farbpipette',
    'black-and-white': 'schwarzweiss-foto',
    'photo-flipper': 'foto-spiegeln',
    'photo-brightener': 'foto-aufhellen',
    'colorize-photo': 'foto-einfaerben',
    'photo-rotator': 'foto-drehen',
  },
  pt: {
    'image-converter': 'conversor-de-imagens',
    'shadow-remover': 'remover-sombras',
    'image-upscaler': 'ampliar-imagem',
    'photo-darkener': 'escurecer-foto',
    'photo-inverter': 'inverter-foto',
    'photo-blur': 'desfocar-foto',
    'round-cropper': 'recorte-redondo',
    'photo-cropper': 'recortar-foto',
    'color-eyedropper': 'conta-gotas-de-cor',
    'black-and-white': 'foto-preto-e-branco',
    'photo-flipper': 'espelhar-foto',
    'photo-brightener': 'clarear-foto',
    'colorize-photo': 'colorir-foto',
    'photo-rotator': 'girar-foto',
  },
};

const LOCALIZED_TOOL_COPY: Partial<Record<Locale, Partial<Record<ImageToolId, Partial<ImageToolDefinition>>>>> = {
  es: {
    'image-converter': {
      title: 'Conversor de formatos de imagen',
      shortTitle: 'Conversor de imágenes',
      eyebrow: 'Convertir',
      description: 'Convierte JPG, PNG, WebP, AVIF, GIF o BMP cuando el navegador pueda leerlos.',
      outputNote: 'PNG siempre está disponible. JPEG, WebP y AVIF solo aparecen si este navegador puede exportarlos.',
      seoTitle: 'Conversor de formatos de imagen online gratis',
      seoDescription:
        'Convierte imágenes online en tu navegador. Sube JPG, PNG, WebP, AVIF, GIF o BMP compatibles y exporta PNG, JPEG, WebP o AVIF.',
      headline: 'Convierte formatos de imagen en el navegador.',
      heroBody: 'Añade una imagen o un lote, elige el formato de salida y descarga archivos convertidos sin subir los originales.',
    },
    'shadow-remover': {
      title: 'Eliminador de sombras',
      shortTitle: 'Eliminar sombras',
      eyebrow: 'Luz',
      description: 'Aclara sombras leves en caras, documentos y productos antes de exportar.',
      outputNote: 'Funciona mejor con sombras suaves. Si la sombra tapa detalles, conviene repetir la foto.',
    },
    'image-upscaler': {
      title: 'Mejorador de resolución de imagen',
      shortTitle: 'Mejorar imagen',
      eyebrow: 'Aumentar',
      description: 'Crea un PNG más grande en 2x o 4x para revisar y reutilizar.',
      outputNote: 'La salida es un PNG de mayor tamaño.',
    },
    'photo-darkener': {
      title: 'Oscurecer foto',
      shortTitle: 'Oscurecer foto',
      eyebrow: 'Tono',
      description: 'Reduce el brillo de una foto demasiado clara.',
      outputNote: 'Útil cuando una foto o escaneo quedó lavado.',
    },
    'photo-inverter': {
      title: 'Invertir foto',
      shortTitle: 'Invertir foto',
      eyebrow: 'Invertir',
      description: 'Invierte los colores de una imagen para revisión rápida o uso creativo.',
      outputNote: 'El PNG exportado conserva los colores invertidos.',
    },
    'photo-blur': {
      title: 'Desenfocar foto',
      shortTitle: 'Desenfocar',
      eyebrow: 'Desenfoque',
      description: 'Añade desenfoque a una imagen antes de compartirla o reutilizarla.',
      outputNote: 'Puedes ajustar la intensidad antes de exportar.',
    },
    'round-cropper': {
      title: 'Recorte redondo',
      shortTitle: 'Recorte redondo',
      eyebrow: 'Círculo',
      description: 'Crea un recorte circular para perfiles, contactos o avatares.',
      outputNote: 'La exportación es un PNG transparente con el recorte aplicado.',
    },
    'photo-cropper': {
      title: 'Recortador de fotos',
      shortTitle: 'Recortar foto',
      eyebrow: 'Recortar',
      description: 'Recorta una foto en formato cuadrado, vertical, horizontal o panorámico.',
      outputNote: 'Para fotos oficiales, usa el editor de Passlens con presets de documento.',
    },
    'color-eyedropper': {
      title: 'Cuentagotas de color',
      shortTitle: 'Cuentagotas',
      eyebrow: 'Color',
      description: 'Haz clic en la vista previa para leer un color de la imagen.',
      outputNote: 'El color elegido se muestra como muestra, HEX y RGBA.',
    },
    'black-and-white': {
      title: 'Editor de foto en blanco y negro',
      shortTitle: 'Blanco y negro',
      eyebrow: 'Grises',
      description: 'Convierte una foto a blanco y negro.',
      outputNote: 'La exportación conserva el resultado en escala de grises como PNG.',
    },
    'photo-flipper': {
      title: 'Voltear foto',
      shortTitle: 'Voltear foto',
      eyebrow: 'Espejo',
      description: 'Voltea una imagen horizontal o verticalmente.',
      outputNote: 'Útil para previsualizaciones de productos y perfiles no oficiales.',
    },
    'photo-brightener': {
      title: 'Aclarar foto',
      shortTitle: 'Aclarar foto',
      eyebrow: 'Tono',
      description: 'Aclara una imagen oscura con un ajuste rápido.',
      outputNote: 'Funciona mejor cuando todavía hay detalle visible.',
    },
    'colorize-photo': {
      title: 'Colorear foto',
      shortTitle: 'Colorear foto',
      eyebrow: 'Tinte',
      description: 'Añade un tinte controlado a una foto de estilo blanco y negro.',
      outputNote: 'Es un efecto de tinte, no una restauración histórica.',
    },
    'photo-rotator': {
      title: 'Rotar foto',
      shortTitle: 'Rotar foto',
      eyebrow: 'Girar',
      description: 'Gira una foto por ángulos pequeños o grandes y exporta el resultado.',
      outputNote: 'Para enderezar fotos oficiales, el editor principal añade guías de documento.',
    },
  },
  fr: {
    'image-converter': {
      title: 'Convertisseur de formats d’image',
      shortTitle: 'Convertisseur image',
      eyebrow: 'Convertir',
      description: 'Convertissez JPG, PNG, WebP, AVIF, GIF ou BMP lorsque le navigateur peut les lire.',
      outputNote: 'PNG est toujours disponible. JPEG, WebP et AVIF apparaissent seulement si ce navigateur peut les exporter.',
      seoTitle: 'Convertisseur de formats d’image en ligne gratuit',
      seoDescription:
        'Convertissez des images en ligne dans votre navigateur. Importez des JPG, PNG, WebP, AVIF, GIF ou BMP compatibles et exportez en PNG, JPEG, WebP ou AVIF.',
      headline: 'Convertissez des formats d’image dans le navigateur.',
      heroBody: 'Ajoutez une image ou un lot, choisissez le format de sortie et téléchargez les fichiers convertis sans importer les originaux.',
    },
    'shadow-remover': {
      title: 'Suppresseur d’ombres',
      shortTitle: 'Supprimer les ombres',
      eyebrow: 'Lumière',
      description: 'Éclaircissez les ombres légères sur un visage, un document ou un produit avant l’export.',
      outputNote: 'Fonctionne surtout sur les ombres douces. Si un détail est masqué, reprenez la photo.',
    },
    'image-upscaler': {
      title: 'Agrandisseur d’image',
      shortTitle: 'Agrandir image',
      eyebrow: 'Agrandir',
      description: 'Créez un PNG plus grand en 2x ou 4x pour le vérifier et le réutiliser.',
      outputNote: 'La sortie est un PNG plus grand.',
    },
    'photo-darkener': {
      title: 'Assombrir une photo',
      shortTitle: 'Assombrir',
      eyebrow: 'Ton',
      description: 'Réduisez la luminosité d’une photo trop claire.',
      outputNote: 'Utile quand une photo ou un scan paraît délavé.',
    },
    'photo-inverter': {
      title: 'Inverser une photo',
      shortTitle: 'Inverser',
      eyebrow: 'Inverser',
      description: 'Inversez les couleurs d’une image pour une vérification rapide ou un usage créatif.',
      outputNote: 'Le PNG exporté conserve les couleurs inversées.',
    },
    'photo-blur': {
      title: 'Flouter une photo',
      shortTitle: 'Flouter',
      eyebrow: 'Flou',
      description: 'Ajoutez un flou à une image avant de la partager ou de la réutiliser.',
      outputNote: 'L’intensité du flou se règle avant l’export.',
    },
    'round-cropper': {
      title: 'Recadrage rond',
      shortTitle: 'Recadrage rond',
      eyebrow: 'Cercle',
      description: 'Créez un recadrage circulaire pour profils, contacts ou avatars.',
      outputNote: 'L’export est un PNG transparent avec le recadrage rond appliqué.',
    },
    'photo-cropper': {
      title: 'Recadrer une photo',
      shortTitle: 'Recadrer',
      eyebrow: 'Recadrer',
      description: 'Recadrez une photo en carré, portrait, paysage ou format large.',
      outputNote: 'Pour les photos officielles, utilisez l’éditeur Passlens avec les presets de document.',
    },
    'color-eyedropper': {
      title: 'Pipette couleur',
      shortTitle: 'Pipette',
      eyebrow: 'Couleur',
      description: 'Cliquez sur l’aperçu pour lire une couleur dans l’image.',
      outputNote: 'La couleur choisie apparaît avec un échantillon, HEX et RGBA.',
    },
    'black-and-white': {
      title: 'Éditeur photo noir et blanc',
      shortTitle: 'Noir et blanc',
      eyebrow: 'Gris',
      description: 'Convertissez une photo en noir et blanc.',
      outputNote: 'L’export garde le résultat en niveaux de gris au format PNG.',
    },
    'photo-flipper': {
      title: 'Retourner une photo',
      shortTitle: 'Retourner',
      eyebrow: 'Miroir',
      description: 'Retournez une image horizontalement ou verticalement.',
      outputNote: 'Utile pour les aperçus produit et les images de profil non officielles.',
    },
    'photo-brightener': {
      title: 'Éclaircir une photo',
      shortTitle: 'Éclaircir',
      eyebrow: 'Ton',
      description: 'Éclaircissez une image sombre avec un réglage rapide.',
      outputNote: 'Fonctionne mieux lorsque les détails restent visibles.',
    },
    'colorize-photo': {
      title: 'Coloriser une photo',
      shortTitle: 'Coloriser',
      eyebrow: 'Teinte',
      description: 'Ajoutez une teinte contrôlée à une image de style noir et blanc.',
      outputNote: 'C’est un effet de teinte, pas une restauration historique.',
      headline: 'Quand coloriser une photo',
      heroBody:
        'Utilisez cet outil pour donner une teinte légère à une image noir et blanc ou à un visuel de test. Pour une photo d’identité, gardez les couleurs naturelles et vérifiez ensuite le format dans Passlens.',
      whenToUse: [
        'Une ancienne image ou un aperçu noir et blanc a besoin d’une teinte simple.',
        'Vous voulez tester une couleur avant de garder une version PNG.',
        'La photo finale n’est pas destinée à un document officiel.',
      ],
      tips: [
        'Restez léger : une teinte trop forte donne vite un résultat artificiel.',
        'N’utilisez pas la colorisation pour une photo de passeport, visa ou carte d’identité.',
        'Si le fichier doit servir à une demande officielle, ouvrez ensuite l’éditeur Passlens et choisissez le preset du document.',
      ],
    },
    'photo-rotator': {
      title: 'Faire pivoter une photo',
      shortTitle: 'Pivoter',
      eyebrow: 'Rotation',
      description: 'Faites pivoter une photo par petits ou grands angles et exportez le résultat.',
      outputNote: 'Pour les photos officielles, l’éditeur principal ajoute les repères de document.',
    },
  },
  de: {
    'image-converter': {
      title: 'Bildformat-Konverter',
      shortTitle: 'Bildkonverter',
      eyebrow: 'Konvertieren',
      description: 'Konvertieren Sie JPG, PNG, WebP, AVIF, GIF oder BMP, wenn der Browser sie lesen kann.',
      outputNote: 'PNG ist immer verfügbar. JPEG, WebP und AVIF erscheinen nur, wenn dieser Browser sie exportieren kann.',
      seoTitle: 'Kostenloser Bildformat-Konverter online',
      seoDescription:
        'Konvertieren Sie Bilder online im Browser. Unterstützte JPG-, PNG-, WebP-, AVIF-, GIF- und BMP-Dateien lassen sich als PNG, JPEG, WebP oder AVIF exportieren.',
      headline: 'Bildformate im Browser konvertieren.',
      heroBody: 'Laden Sie ein Bild oder einen Stapel hoch, wählen Sie das Zielformat und laden Sie die konvertierten Dateien herunter, ohne die Originale hochzuladen.',
    },
    'shadow-remover': {
      title: 'Schattenentferner',
      shortTitle: 'Schatten entfernen',
      eyebrow: 'Licht',
      description: 'Hellen Sie leichte Schatten auf Gesichtern, Dokumenten oder Produkten vor dem Export auf.',
      outputNote: 'Am besten für weiche Schatten. Wenn Details verdeckt sind, nehmen Sie das Foto neu auf.',
    },
    'image-upscaler': {
      title: 'Bild hochskalieren',
      shortTitle: 'Bild skalieren',
      eyebrow: 'Hochskalieren',
      description: 'Erstellen Sie ein größeres PNG in 2x oder 4x zur Prüfung und Wiederverwendung.',
      outputNote: 'Die Ausgabe ist ein größeres PNG.',
    },
    'photo-darkener': {
      title: 'Foto abdunkeln',
      shortTitle: 'Abdunkeln',
      eyebrow: 'Ton',
      description: 'Reduzieren Sie die Helligkeit eines zu hellen Fotos.',
      outputNote: 'Nützlich, wenn ein Foto oder Scan ausgewaschen wirkt.',
    },
    'photo-inverter': {
      title: 'Foto invertieren',
      shortTitle: 'Invertieren',
      eyebrow: 'Invertieren',
      description: 'Kehren Sie die Farben eines Bildes für Prüfung oder kreative Nutzung um.',
      outputNote: 'Das exportierte PNG behält die invertierten Farben.',
    },
    'photo-blur': {
      title: 'Foto weichzeichnen',
      shortTitle: 'Weichzeichnen',
      eyebrow: 'Unschärfe',
      description: 'Weichzeichnen Sie ein Bild vor dem Teilen, Speichern oder Wiederverwenden.',
      outputNote: 'Die Stärke lässt sich vor dem Export anpassen.',
    },
    'round-cropper': {
      title: 'Rund zuschneiden',
      shortTitle: 'Runder Zuschnitt',
      eyebrow: 'Kreis',
      description: 'Erstellen Sie einen runden Zuschnitt für Profile, Kontakte oder Avatare.',
      outputNote: 'Der Export ist ein transparentes PNG mit rundem Zuschnitt.',
    },
    'photo-cropper': {
      title: 'Foto zuschneiden',
      shortTitle: 'Zuschneiden',
      eyebrow: 'Zuschnitt',
      description: 'Schneiden Sie ein Foto quadratisch, hochkant, quer oder breit zu.',
      outputNote: 'Für offizielle Fotos nutzen Sie den Passlens-Editor mit Dokument-Presets.',
    },
    'color-eyedropper': {
      title: 'Farbpipette',
      shortTitle: 'Farbpipette',
      eyebrow: 'Farbe',
      description: 'Klicken Sie in die Vorschau, um eine Farbe aus dem Bild zu lesen.',
      outputNote: 'Die gewählte Farbe erscheint als Farbfeld, HEX und RGBA.',
    },
    'black-and-white': {
      title: 'Schwarz-Weiß-Fotoeditor',
      shortTitle: 'Schwarz-Weiß',
      eyebrow: 'Grau',
      description: 'Wandeln Sie ein Foto in Schwarz-Weiß um.',
      outputNote: 'Der Export behält das Graustufen-Ergebnis als PNG.',
    },
    'photo-flipper': {
      title: 'Foto spiegeln',
      shortTitle: 'Spiegeln',
      eyebrow: 'Spiegel',
      description: 'Spiegeln Sie ein Bild horizontal oder vertikal.',
      outputNote: 'Nützlich für Produktvorschauen und nicht offizielle Profilbilder.',
    },
    'photo-brightener': {
      title: 'Foto aufhellen',
      shortTitle: 'Aufhellen',
      eyebrow: 'Ton',
      description: 'Hellen Sie ein dunkles Bild mit einer schnellen Anpassung auf.',
      outputNote: 'Funktioniert am besten, wenn noch Details sichtbar sind.',
    },
    'colorize-photo': {
      title: 'Foto einfärben',
      shortTitle: 'Einfärben',
      eyebrow: 'Tönung',
      description: 'Fügen Sie einem Schwarz-Weiß-Stil eine kontrollierte Tönung hinzu.',
      outputNote: 'Das ist ein Tönungseffekt, keine historische Restaurierung.',
    },
    'photo-rotator': {
      title: 'Foto drehen',
      shortTitle: 'Drehen',
      eyebrow: 'Rotation',
      description: 'Drehen Sie ein Foto um kleine oder große Winkel und exportieren Sie das Ergebnis.',
      outputNote: 'Für offizielle Fotos bietet der Haupteditor Dokument-Hilfslinien.',
    },
  },
  pt: {
    'image-converter': {
      title: 'Conversor de formatos de imagem',
      shortTitle: 'Conversor de imagens',
      eyebrow: 'Converter',
      description: 'Converta JPG, PNG, WebP, AVIF, GIF ou BMP quando o navegador conseguir abrir o arquivo.',
      outputNote: 'PNG está sempre disponível. JPEG, WebP e AVIF aparecem apenas se este navegador conseguir exportar.',
      seoTitle: 'Conversor de formatos de imagem online gratuito',
      seoDescription:
        'Converta imagens online no navegador. Envie JPG, PNG, WebP, AVIF, GIF ou BMP compatíveis e exporte em PNG, JPEG, WebP ou AVIF.',
      headline: 'Converta formatos de imagem no navegador.',
      heroBody: 'Adicione uma imagem ou um lote, escolha o formato de saída e baixe os arquivos convertidos sem enviar os originais.',
    },
    'shadow-remover': {
      title: 'Removedor de sombras',
      shortTitle: 'Remover sombras',
      eyebrow: 'Luz',
      description: 'Clareie sombras leves em rosto, documento ou produto antes de exportar.',
      outputNote: 'Funciona melhor com sombras suaves. Se a sombra cobre detalhes, refaça a foto.',
    },
    'image-upscaler': {
      title: 'Ampliador de imagem',
      shortTitle: 'Ampliar imagem',
      eyebrow: 'Ampliar',
      description: 'Crie um PNG maior em 2x ou 4x para revisar e reutilizar.',
      outputNote: 'A saída é um PNG maior.',
    },
    'photo-darkener': {
      title: 'Escurecer foto',
      shortTitle: 'Escurecer',
      eyebrow: 'Tom',
      description: 'Reduza o brilho de uma foto clara demais.',
      outputNote: 'Útil quando uma foto ou digitalização ficou lavada.',
    },
    'photo-inverter': {
      title: 'Inverter foto',
      shortTitle: 'Inverter',
      eyebrow: 'Inverter',
      description: 'Inverta as cores de uma imagem para revisão rápida ou uso criativo.',
      outputNote: 'O PNG exportado mantém as cores invertidas.',
    },
    'photo-blur': {
      title: 'Desfocar foto',
      shortTitle: 'Desfocar',
      eyebrow: 'Desfoque',
      description: 'Aplique desfoque a uma imagem antes de compartilhar ou reutilizar.',
      outputNote: 'A intensidade pode ser ajustada antes da exportação.',
    },
    'round-cropper': {
      title: 'Recorte redondo',
      shortTitle: 'Recorte redondo',
      eyebrow: 'Círculo',
      description: 'Crie um recorte circular para perfis, contatos ou avatares.',
      outputNote: 'A exportação é um PNG transparente com o recorte aplicado.',
    },
    'photo-cropper': {
      title: 'Recortador de fotos',
      shortTitle: 'Recortar foto',
      eyebrow: 'Recortar',
      description: 'Recorte uma foto em formato quadrado, retrato, paisagem ou largo.',
      outputNote: 'Para fotos oficiais, use o editor Passlens com presets de documento.',
    },
    'color-eyedropper': {
      title: 'Conta-gotas de cor',
      shortTitle: 'Conta-gotas',
      eyebrow: 'Cor',
      description: 'Clique na prévia para ler uma cor da imagem.',
      outputNote: 'A cor escolhida aparece como amostra, HEX e RGBA.',
    },
    'black-and-white': {
      title: 'Editor de foto em preto e branco',
      shortTitle: 'Preto e branco',
      eyebrow: 'Cinza',
      description: 'Converta uma foto para preto e branco.',
      outputNote: 'A exportação mantém o resultado em escala de cinza como PNG.',
    },
    'photo-flipper': {
      title: 'Espelhar foto',
      shortTitle: 'Espelhar',
      eyebrow: 'Espelho',
      description: 'Espelhe uma imagem na horizontal ou vertical.',
      outputNote: 'Útil para prévias de produtos e imagens de perfil não oficiais.',
    },
    'photo-brightener': {
      title: 'Clarear foto',
      shortTitle: 'Clarear',
      eyebrow: 'Tom',
      description: 'Clareie uma imagem escura com um ajuste rápido.',
      outputNote: 'Funciona melhor quando ainda há detalhe visível.',
    },
    'colorize-photo': {
      title: 'Colorir foto',
      shortTitle: 'Colorir',
      eyebrow: 'Tonalidade',
      description: 'Adicione uma tonalidade controlada a uma imagem em estilo preto e branco.',
      outputNote: 'É um efeito de tonalidade, não restauração histórica.',
    },
    'photo-rotator': {
      title: 'Girar foto',
      shortTitle: 'Girar foto',
      eyebrow: 'Rotação',
      description: 'Gire uma foto em ângulos pequenos ou grandes e exporte o resultado.',
      outputNote: 'Para fotos oficiais, o editor principal mostra guias de documento.',
      headline: 'Quando girar a foto antes de exportar',
      heroBody:
        'Gire a imagem quando a câmera ficou levemente inclinada, quando um documento foi digitalizado de lado ou quando a foto precisa ficar reta antes do corte.',
      whenToUse: [
        'A linha dos ombros, o fundo ou a borda do documento ficou torta.',
        'Você precisa corrigir a orientação antes de recortar a foto.',
        'A imagem será revisada no Passlens depois da rotação.',
      ],
      tips: [
        'Corrija primeiro a rotação e só depois faça o corte final.',
        'Evite inclinações grandes em fotos oficiais; normalmente é melhor refazer a foto.',
        'Depois de girar, confira se a cabeça ainda cabe dentro das guias do preset.',
      ],
    },
  },
  ar: {
    'image-converter': {
      title: 'محول صيغ الصور',
      shortTitle: 'محول الصور',
      eyebrow: 'تحويل',
      description: 'حوّل JPG أو PNG أو WebP أو AVIF أو GIF أو BMP عندما يستطيع المتصفح قراءتها.',
      outputNote: 'PNG متاح دائمًا. تظهر JPEG وWebP وAVIF فقط عندما يستطيع هذا المتصفح تصديرها.',
      seoTitle: 'محول صيغ الصور مجانًا عبر الإنترنت',
      seoDescription:
        'حوّل الصور عبر الإنترنت داخل المتصفح. ارفع ملفات JPG وPNG وWebP وAVIF وGIF وBMP المدعومة وصدّرها إلى PNG أو JPEG أو WebP أو AVIF.',
      headline: 'حوّل صيغ الصور داخل المتصفح.',
      heroBody: 'أضف صورة واحدة أو مجموعة صور، اختر صيغة الإخراج، ثم نزّل الملفات المحولة بدون رفع الصور الأصلية.',
    },
    'shadow-remover': {
      title: 'مزيل الظلال',
      shortTitle: 'إزالة الظلال',
      eyebrow: 'إضاءة',
      description: 'خفف الظلال الخفيفة على الوجه أو المستند أو المنتج قبل التصدير.',
      outputNote: 'يناسب الظلال الخفيفة. إذا حجبت الظلال التفاصيل، التقط صورة جديدة.',
    },
    'image-upscaler': {
      title: 'مكبر الصور',
      shortTitle: 'تكبير الصورة',
      eyebrow: 'تكبير',
      description: 'أنشئ PNG أكبر بمعدل 2x أو 4x للمراجعة وإعادة الاستخدام.',
      outputNote: 'النتيجة ملف PNG أكبر.',
    },
    'photo-darkener': {
      title: 'تغميق الصورة',
      shortTitle: 'تغميق',
      eyebrow: 'درجة اللون',
      description: 'قلل سطوع صورة شديدة الإضاءة.',
      outputNote: 'مفيد عندما تبدو الصورة أو المسح باهتًا.',
    },
    'photo-inverter': {
      title: 'عكس ألوان الصورة',
      shortTitle: 'عكس الألوان',
      eyebrow: 'عكس',
      description: 'اعكس ألوان الصورة للمراجعة السريعة أو الاستخدام الإبداعي.',
      outputNote: 'يحافظ PNG المصدر على الألوان المعكوسة.',
    },
    'photo-blur': {
      title: 'تمويه الصورة',
      shortTitle: 'تمويه',
      eyebrow: 'تمويه',
      description: 'أضف تمويهًا إلى الصورة قبل مشاركتها أو إعادة استخدامها.',
      outputNote: 'يمكن ضبط قوة التمويه قبل التصدير.',
    },
    'round-cropper': {
      title: 'قص دائري',
      shortTitle: 'قص دائري',
      eyebrow: 'دائرة',
      description: 'أنشئ قصًا دائريًا للملفات الشخصية أو جهات الاتصال أو الصور الرمزية.',
      outputNote: 'التصدير هو PNG شفاف مع القص الدائري.',
    },
    'photo-cropper': {
      title: 'قص الصورة',
      shortTitle: 'قص الصورة',
      eyebrow: 'قص',
      description: 'قص صورة بشكل مربع أو عمودي أو أفقي أو عريض.',
      outputNote: 'للصور الرسمية، استخدم محرر Passlens مع مقاسات المستندات.',
    },
    'color-eyedropper': {
      title: 'قطارة اللون',
      shortTitle: 'قطارة اللون',
      eyebrow: 'لون',
      description: 'انقر على المعاينة لقراءة لون من الصورة.',
      outputNote: 'يظهر اللون المختار كعينة وقيمة HEX وRGBA.',
    },
    'black-and-white': {
      title: 'محرر الصور بالأبيض والأسود',
      shortTitle: 'أبيض وأسود',
      eyebrow: 'رمادي',
      description: 'حوّل الصورة إلى أبيض وأسود.',
      outputNote: 'يحافظ التصدير على نتيجة التدرج الرمادي كملف PNG.',
    },
    'photo-flipper': {
      title: 'قلب الصورة',
      shortTitle: 'قلب الصورة',
      eyebrow: 'مرآة',
      description: 'اقلب الصورة أفقيًا أو عموديًا.',
      outputNote: 'مفيد لمعاينات المنتجات وصور الملفات غير الرسمية.',
    },
    'photo-brightener': {
      title: 'تفتيح الصورة',
      shortTitle: 'تفتيح',
      eyebrow: 'درجة اللون',
      description: 'افتح صورة داكنة بتعديل سريع.',
      outputNote: 'يعمل أفضل عندما تظل التفاصيل مرئية.',
    },
    'colorize-photo': {
      title: 'تلوين الصورة',
      shortTitle: 'تلوين',
      eyebrow: 'تلوين',
      description: 'أضف لونًا خفيفًا ومضبوطًا إلى صورة بنمط أبيض وأسود.',
      outputNote: 'هذا تأثير تلوين، وليس ترميمًا تاريخيًا.',
    },
    'photo-rotator': {
      title: 'تدوير الصورة',
      shortTitle: 'تدوير',
      eyebrow: 'تدوير',
      description: 'دوّر الصورة بزوايا صغيرة أو كبيرة وصدّر النتيجة.',
      outputNote: 'للصور الرسمية، يعرض المحرر الرئيسي خطوط إرشاد المستند.',
    },
  },
  'zh-Hans': {
    'image-converter': {
      title: '图片格式转换器',
      shortTitle: '图片转换器',
      eyebrow: '转换',
      description: '在浏览器可解码时转换 JPG、PNG、WebP、AVIF、GIF 或 BMP 文件。',
      outputNote: 'PNG 始终可用。JPEG、WebP 和 AVIF 只在当前浏览器支持导出时显示。',
      seoTitle: '免费在线图片格式转换器',
      seoDescription:
        '在浏览器中在线转换图片。上传受支持的 JPG、PNG、WebP、AVIF、GIF 或 BMP，并导出为 PNG、JPEG、WebP 或 AVIF。',
      headline: '在浏览器中转换图片格式。',
      heroBody: '放入一张图片或一批图片，选择输出格式，然后下载转换后的文件，无需上传原图。',
    },
    'shadow-remover': {
      title: '阴影去除工具',
      shortTitle: '去除阴影',
      eyebrow: '光线',
      description: '在导出前减轻人像、文档或商品图中的轻微阴影。',
      outputNote: '适合轻微阴影。如果阴影遮住细节，建议重新拍摄。',
    },
    'image-upscaler': {
      title: '图片放大工具',
      shortTitle: '图片放大',
      eyebrow: '放大',
      description: '生成 2x 或 4x 的更大 PNG，方便检查和复用。',
      outputNote: '输出为更大尺寸的 PNG。',
    },
    'photo-darkener': {
      title: '照片调暗工具',
      shortTitle: '调暗照片',
      eyebrow: '色调',
      description: '降低过亮照片的亮度。',
      outputNote: '适合照片或扫描件发白的情况。',
    },
    'photo-inverter': {
      title: '照片反色工具',
      shortTitle: '照片反色',
      eyebrow: '反色',
      description: '反转图片颜色，用于快速检查或创意处理。',
      outputNote: '导出的 PNG 会保留反色效果。',
    },
    'photo-blur': {
      title: '照片模糊工具',
      shortTitle: '照片模糊',
      eyebrow: '模糊',
      description: '在分享或复用前为图片添加模糊效果。',
      outputNote: '导出前可以调整模糊强度。',
    },
    'round-cropper': {
      title: '圆形裁剪工具',
      shortTitle: '圆形裁剪',
      eyebrow: '圆形',
      description: '为头像、联系人照片或个人资料图片制作圆形裁剪。',
      outputNote: '导出为带圆形裁剪的透明 PNG。',
    },
    'photo-cropper': {
      title: '照片裁剪工具',
      shortTitle: '裁剪照片',
      eyebrow: '裁剪',
      description: '将照片裁成方形、竖版、横版或宽幅。',
      outputNote: '官方证件照请使用带文档预设的 Passlens 编辑器。',
      headline: '证件照裁剪和普通裁剪的区别',
      heroBody:
        '普通裁剪只关心画面比例。证件照裁剪还要检查头部高度、眼睛位置、背景和最终尺寸。这个工具适合先整理图片，正式导出请回到 Passlens 证件照编辑器。',
      whenToUse: [
        '照片边缘有多余空间，需要先裁成更干净的构图。',
        '你想做头像、资料图或普通图片，而不是直接提交证件申请。',
        '后续还会用 Passlens 的国家预设检查尺寸和头部位置。',
      ],
      tips: [
        '不要把头顶、下巴或肩膀裁得太紧，证件照通常需要留出检查空间。',
        '先裁掉明显多余的边，再用证件照预设做最终尺寸。',
        '如果照片用于护照、签证或身份证，最后一定要在 Passlens 编辑器里重新检查。',
      ],
    },
    'color-eyedropper': {
      title: '颜色取样器',
      shortTitle: '取色器',
      eyebrow: '颜色',
      description: '点击预览，从图片中读取颜色。',
      outputNote: '选中的颜色会显示为色块、HEX 和 RGBA。',
    },
    'black-and-white': {
      title: '黑白照片编辑器',
      shortTitle: '黑白照片',
      eyebrow: '灰度',
      description: '将照片转换为黑白效果。',
      outputNote: '导出的 PNG 会保留灰度结果。',
    },
    'photo-flipper': {
      title: '照片翻转工具',
      shortTitle: '翻转照片',
      eyebrow: '镜像',
      description: '水平或垂直翻转图片。',
      outputNote: '适合商品预览和非官方头像图片。',
    },
    'photo-brightener': {
      title: '照片调亮工具',
      shortTitle: '调亮照片',
      eyebrow: '色调',
      description: '用快速调整提亮偏暗图片。',
      outputNote: '在细节仍可见时效果最好。',
    },
    'colorize-photo': {
      title: '照片上色工具',
      shortTitle: '照片上色',
      eyebrow: '色调',
      description: '为黑白风格图片添加可控色调。',
      outputNote: '这是色调效果，不是历史照片修复。',
    },
    'photo-rotator': {
      title: '照片旋转工具',
      shortTitle: '旋转照片',
      eyebrow: '旋转',
      description: '按小角度或大角度旋转照片并导出结果。',
      outputNote: '官方证件照请在主编辑器中检查文档辅助线。',
    },
  },
};

const LOCALIZED_TOOL_DETAIL_COPY: Partial<Record<Locale, Pick<ImageToolDefinition, 'whenToUse' | 'tips'>>> = {
  es: {
    whenToUse: [
      'La imagen necesita un ajuste rápido antes de descargarla.',
      'Quieres revisar una versión PNG antes de usarla en otro lugar.',
      'Después vas a comprobar tamaño, fondo o recorte en el editor de Passlens.',
    ],
    tips: [
      'Empieza con un ajuste suave y revisa la imagen a tamaño real.',
      'Si la foto está borrosa o mal iluminada, repetirla suele dar mejor resultado.',
      'Para pasaporte, visa o documento, termina siempre con un preset oficial.',
    ],
  },
  fr: {
    whenToUse: [
      'L’image a besoin d’une retouche rapide avant le téléchargement.',
      'Vous voulez vérifier une version PNG avant de l’utiliser ailleurs.',
      'Vous passerez ensuite dans l’éditeur Passlens pour contrôler format, fond ou recadrage.',
    ],
    tips: [
      'Commencez par un réglage léger et vérifiez l’image en taille réelle.',
      'Si la photo est floue ou mal éclairée, la reprendre donne souvent un meilleur résultat.',
      'Pour un passeport, un visa ou une pièce d’identité, terminez avec un preset officiel.',
    ],
  },
  de: {
    whenToUse: [
      'Das Bild braucht vor dem Download eine schnelle Korrektur.',
      'Sie möchten eine PNG-Version prüfen, bevor Sie sie weiterverwenden.',
      'Danach sollen Größe, Hintergrund oder Zuschnitt im Passlens-Editor kontrolliert werden.',
    ],
    tips: [
      'Beginnen Sie mit einer leichten Anpassung und prüfen Sie das Bild in Originalgröße.',
      'Wenn das Foto unscharf oder schlecht beleuchtet ist, ist eine neue Aufnahme meist besser.',
      'Für Pass, Visum oder Ausweis schließen Sie mit einem offiziellen Preset ab.',
    ],
  },
  pt: {
    whenToUse: [
      'A imagem precisa de um ajuste rápido antes do download.',
      'Você quer revisar uma versão PNG antes de usar em outro lugar.',
      'Depois será preciso conferir tamanho, fundo ou corte no editor Passlens.',
    ],
    tips: [
      'Comece com um ajuste leve e revise a imagem em tamanho real.',
      'Se a foto estiver desfocada ou mal iluminada, refazer costuma ser melhor.',
      'Para passaporte, visto ou documento, finalize com um preset oficial.',
    ],
  },
  ar: {
    whenToUse: [
      'تحتاج الصورة إلى تعديل سريع قبل التنزيل.',
      'تريد مراجعة نسخة PNG قبل استخدامها في مكان آخر.',
      'ستتحقق لاحقًا من المقاس أو الخلفية أو القص داخل محرر Passlens.',
    ],
    tips: [
      'ابدأ بتعديل خفيف وراجع الصورة بالحجم الكامل.',
      'إذا كانت الصورة ضبابية أو الإضاءة سيئة، فإعادة التصوير غالبًا أفضل.',
      'لجواز السفر أو التأشيرة أو الهوية، أنهِ العمل بمقاس رسمي.',
    ],
  },
  'zh-Hans': {
    whenToUse: [
      '图片在下载前需要快速调整。',
      '你想先检查 PNG 版本，再用于其他地方。',
      '之后还需要在 Passlens 编辑器中检查尺寸、背景或裁剪。',
    ],
    tips: [
      '先使用轻微调整，并按实际大小检查图片。',
      '如果照片模糊或光线很差，重新拍摄通常更好。',
      '用于护照、签证或证件时，最后请使用官方尺寸预设。',
    ],
  },
};

export function getLocalizedImageToolsPageCopy(locale: Locale): ImageToolsPageCopy {
  return IMAGE_TOOLS_PAGE_COPY[locale] ?? IMAGE_TOOLS_PAGE_COPY[DEFAULT_LOCALE];
}

export function getImageToolsRouteSegment(locale: Locale): string {
  return getLocalizedImageToolsPageCopy(locale).routeSegment;
}

export function getImageToolLocalizedSlug(locale: Locale, id: ImageToolId): string {
  return LOCALIZED_TOOL_SLUGS[locale]?.[id] ?? LOCALIZED_TOOL_SLUGS[DEFAULT_LOCALE][id];
}

export const DEFAULT_IMAGE_TOOL_ID: ImageToolId = 'photo-brightener';
export const IMAGE_TOOL_SLUGS = IMAGE_TOOLS.map((tool) => tool.slug);

export function getImageToolBySlug(slug?: string): ImageToolDefinition | undefined {
  if (!slug) return undefined;
  return IMAGE_TOOLS.find((tool) => tool.slug === slug || tool.id === slug);
}

export function getImageToolById(id: ImageToolId): ImageToolDefinition | undefined {
  return IMAGE_TOOLS.find((tool) => tool.id === id);
}

export function getImageToolByLocalizedSlug(locale: Locale, slug: string): ImageToolDefinition | undefined {
  const localizedEntry = Object.entries(LOCALIZED_TOOL_SLUGS[locale] ?? {}).find(
    ([, localizedSlug]) => localizedSlug === slug,
  );
  return localizedEntry ? getImageToolById(localizedEntry[0] as ImageToolId) : getImageToolBySlug(slug);
}

export function getLocalizedImageTool(locale: Locale, idOrTool: ImageToolId | ImageToolDefinition): ImageToolDefinition {
  const base = typeof idOrTool === 'string' ? getImageToolById(idOrTool)! : idOrTool;
  const localized = LOCALIZED_TOOL_COPY[locale]?.[base.id] ?? {};
  const detailCopy = locale === DEFAULT_LOCALE ? undefined : LOCALIZED_TOOL_DETAIL_COPY[locale];
  const title = localized.title ?? base.title;
  const description = localized.description ?? base.description;
  const headline = localized.headline ?? (locale === DEFAULT_LOCALE ? base.headline : title);
  return {
    ...base,
    ...localized,
    title,
    description,
    seoTitle: localized.seoTitle ?? (locale === DEFAULT_LOCALE ? base.seoTitle : title),
    seoDescription: localized.seoDescription ?? description,
    headline,
    heroBody: localized.heroBody ?? (locale === DEFAULT_LOCALE ? base.heroBody : description),
    whenToUse: localized.whenToUse ?? detailCopy?.whenToUse ?? base.whenToUse,
    tips: localized.tips ?? detailCopy?.tips ?? base.tips,
    slug: getImageToolLocalizedSlug(locale, base.id),
  };
}
